// src/components/rcv/CsvUploadTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  Grid,
  TextField,
  Button,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  Tooltip,
  Chip,
  Divider
} from '@mui/material';
import {
  UploadFile,
  SaveOutlined,
  CancelOutlined,
  FilePresent
} from '@mui/icons-material';

// Componentes compartidos
import { CardContainer, LoadingState } from '../shared';

import { documentosService } from '../../services/documentosService';
import { formatearRut, formatearMonto, formatearFecha, getTipoDocLabel } from '../../utils/formatters';

const CsvUploadTab = () => {
  const navigate = useNavigate();
  const [rutEmpresa, setRutEmpresa] = useState('');
  const [dvEmpresa, setDvEmpresa] = useState('');
  const [periodo, setPeriodo] = useState('');
  const [tipoOperacion, setTipoOperacion] = useState('COMPRA');
  const [estadoContab, setEstadoContab] = useState('REGISTRO');
  const [loading, setLoading] = useState(false);
  const [loadingParsing, setLoadingParsing] = useState(false);
  const [loadingCarga, setLoadingCarga] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [periodos, setPeriodos] = useState([]);
  const [documentos, setDocumentos] = useState([]);
  const [documentosSeleccionados, setDocumentosSeleccionados] = useState({});
  const [documentosExistentes, setDocumentosExistentes] = useState({});
  const [csvFile, setCsvFile] = useState(null);
  const { empresaActiva } = useSelector((state) => state.empresa);

  const parsearFecha = (fechaStr) => {
    if (!fechaStr || fechaStr === '' || fechaStr === 'null') return null;
  
    try {
      let fecha;
      
      // Manejar formato "dd/mm/yyyy" o "dd/mm/yyyy hh:mm:ss"
      if (fechaStr.includes('/')) {
        const [dia, mes, anioCompleto] = fechaStr.split('/');
        
        // Extraer solo el año (YYYY) si contiene horas
        const anio = anioCompleto.split(' ')[0];
        
        // Crear objeto Date con componentes de fecha (sin tiempo)
        fecha = new Date(parseInt(anio), parseInt(mes) - 1, parseInt(dia));
      } else if (fechaStr instanceof Date) {
        fecha = fechaStr;
      } else {
        return null;
      }
      
      // Para evitar problemas con el backend, formatear la fecha como YYYY-MM-DD
      // sin componente de tiempo
      const yyyy = fecha.getFullYear();
      const mm = String(fecha.getMonth() + 1).padStart(2, '0'); // Enero es 0
      const dd = String(fecha.getDate()).padStart(2, '0');
      
      return `${yyyy}-${mm}-${dd}`; // Retorna string en formato YYYY-MM-DD
    } catch (error) {
      console.warn(`Error al parsear fecha: ${fechaStr}`, error);
      return null;
    }
  };

  const verificarDocumentosExistentes = useCallback(async () => {
    try {
      setLoading(true);
      
      // Verificar documentos en la base de datos local
      const existentes = await documentosService.getDocumentosPeriodo(rutEmpresa, periodo);
      
      // Crear mapa de documentos existentes (por rut_proveedor+folio)
      const mapaExistentes = {};
      
      documentos.forEach((doc, index) => {
        // Verificar si existe en la base local
        const existe = existentes.some(e => 
          e.rut_proveedor === doc.rut_proveedor && 
          e.folio === doc.folio
        );
        
        if (existe) {
          mapaExistentes[index.toString()] = true;
        }
      });
      
      // Actualizar el estado
      setDocumentosExistentes(mapaExistentes);
      
    } catch (err) {
      console.error("Error al verificar documentos existentes:", err);
    } finally {
      setLoading(false);
    }
  }, [rutEmpresa, periodo, documentos]);

  useEffect(() => {
    if (documentos.length > 0) {
      verificarDocumentosExistentes();
    }
  }, [documentos, verificarDocumentosExistentes]);

  useEffect(() => {
    // Generar opciones de periodos
    // Importar la función de generación de períodos
    import('../../utils/periodos').then(({ generarPeriodos }) => {
      const opciones = generarPeriodos();
      setPeriodos(opciones);
      if (opciones.length > 0) {
        setPeriodo(opciones[0].value);
      }
    });
  }, []);

  // Establecer valores predeterminados de la empresa activa
  useEffect(() => {
    if (empresaActiva) {
      setRutEmpresa(empresaActiva.rut);
      setDvEmpresa(empresaActiva.dv);
    }
  }, [empresaActiva]);

  const handleFileChange = (e) => {
    if (e.target.files.length > 0) {
      setCsvFile(e.target.files[0]);
      setDocumentos([]);
      setDocumentosSeleccionados({});
      setDocumentosExistentes({});
      setError('');
      setSuccess('');
    }
  };

  const parseCSV = (file) => {
    if (!file) {
      setError('Por favor seleccione un archivo CSV');
      return;
    }
  
    setLoadingParsing(true);
    setError('');
    setSuccess('');
    
    // Leer el archivo como texto plano
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvText = e.target.result;
        
        // Dividir el archivo en líneas
        const lines = csvText.split(/\r\n|\n/).filter(line => line.trim() !== '');
        
        if (lines.length < 2) {
          setError('El archivo CSV no contiene suficientes datos');
          setLoadingParsing(false);
          return;
        }
        
        // Obtener los encabezados
        const headers = lines[0].split(';');
  
        // Agrupar documentos por RUT+Folio
        const documentosMap = {};
        
        // Procesar cada línea de datos
        for (let i = 1; i < lines.length; i++) {
          const values = lines[i].split(';');
          
          // Si no tenemos suficientes valores, ignorar la línea
          if (values.length < headers.length) {
            console.warn(`Línea ${i} tiene menos valores que encabezados, ignorando`);
            continue;
          }
          
          // Crear un objeto con los datos
          const rowData = {};
          headers.forEach((header, index) => {
            rowData[header] = values[index] || '';
          });
          
          // Extraer datos clave
          const rutProveedor = rowData['RUT Proveedor'] || '';
          const folio = rowData['Folio'] || '';
          
          // Verificar que tenemos los datos mínimos
          if (!rutProveedor || !folio) continue;
          
          // Procesar RUT/DV
          let dv = '';
          let rut = rutProveedor;
          
          if (rutProveedor.includes('-')) {
            const parts = rutProveedor.split('-');
            rut = parts[0];
            dv = parts[1];
          }
          
          // Crear una clave única
          const docKey = `${rut}-${folio}`;
          
          // Verificar si este documento ya existe en nuestro mapa
          if (!documentosMap[docKey]) {
            // Crear un nuevo documento
            documentosMap[docKey] = {
              tipo_doc: rowData['Tipo Doc'] || '',
              tipo_compra: rowData['Tipo Compra'] || '',
              rut_proveedor: rut,
              dv_proveedor: dv,
              razon_social: rowData['Razon Social'] || '',
              folio: folio,
              fecha_docto: parsearFecha(rowData['Fecha Docto']),
              fecha_recepcion: parsearFecha(rowData['Fecha Recepcion']),
              fecha_acuse: parsearFecha(rowData['Fecha Acuse']),
              monto_exento: parseFloat(rowData['Monto Exento'] || 0),
              monto_neto: parseFloat(rowData['Monto Neto'] || 0),
              monto_iva_recuperable: parseFloat(rowData['Monto IVA Recuperable'] || 0),
              monto_iva_no_recuperable: parseFloat(rowData['Monto Iva No Recuperable'] || 0),
              codigo_iva_no_rec: rowData['Codigo IVA No Rec.'] || '',
              monto_total: parseFloat(rowData['Monto Total'] || 0),
              monto_neto_activo_fijo: parseFloat(rowData['Monto Neto Activo Fijo'] || 0),
              iva_activo_fijo: parseFloat(rowData['IVA Activo Fijo'] || 0),
              iva_uso_comun: parseFloat(rowData['IVA uso Comun'] || 0),
              impto_sin_derecho_credito: parseFloat(rowData['Impto. Sin Derecho a Credito'] || 0),
              iva_no_retenido: parseFloat(rowData['IVA No Retenido'] || 0),
              tabacos_puros: parseFloat(rowData['Tabacos Puros'] || 0),
              tabacos_cigarrillos: parseFloat(rowData['Tabacos Cigarrillos'] || 0),
              tabacos_elaborados: parseFloat(rowData['Tabacos Elaborados'] || 0),
              nce_nde_sobre_fact_compra: rowData['NCE o NDE sobre Fact. de Compra'] || '',
              otros_impuestos: []
            };
          }
          
          // Verificar si esta fila tiene otro impuesto
          const codigoOtroImpuesto = rowData['Codigo Otro Impuesto'];
          if (codigoOtroImpuesto && codigoOtroImpuesto.trim() !== '') {
            // Agregar este impuesto al documento
            documentosMap[docKey].otros_impuestos.push({
              codigo_impuesto: codigoOtroImpuesto,
              valor_impuesto: parseFloat(rowData['Valor Otro Impuesto'] || 0),
              tasa_impuesto: parseFloat(rowData['Tasa Otro Impuesto'] || 0)
            });
          }
        }
        
        // Convertir el mapa a una lista de documentos
        const documentos = Object.values(documentosMap);
        
        if (documentos.length === 0) {
          setError('No se encontraron documentos válidos en el CSV');
          setLoadingParsing(false);
          return;
        }
        
        setDocumentos(documentos);
        setSuccess(`CSV procesado correctamente. Se encontraron ${documentos.length} documentos.`);
      } catch (err) {
        console.error("Error al procesar CSV:", err);
        setError(`Error al procesar el archivo: ${err.message}`);
      } finally {
        setLoadingParsing(false);
      }
    };
    
    reader.onerror = () => {
      setError('Error al leer el archivo CSV');
      setLoadingParsing(false);
    };
    
    reader.readAsText(file);
  };

  const handleCargarDocumentos = async () => {
    // Obtener solo los documentos seleccionados que no existen en la BD
    const docsParaCargar = Object.keys(documentosSeleccionados)
      .filter(id => documentosSeleccionados[id] && !documentosExistentes[id])
      .map(id => {
        const doc = documentos.find((d, index) => index.toString() === id);
        return doc;
      });
  
    if (docsParaCargar.length === 0) {
      setError('No hay documentos seleccionados para cargar o todos ya existen en la base de datos');
      return;
    }
  
    setLoadingCarga(true);
    setError('');
    
    try {
      // Cargar documentos seleccionados
      /*const _resultado = */await documentosService.cargarDocumentos({
        rut_empresa: rutEmpresa,
        dv_empresa: dvEmpresa,
        periodo: periodo,
        tipo_operacion: tipoOperacion,
        estado_contab: estadoContab,
        documentos_seleccionados: docsParaCargar
      });
      
      setSuccess(`Se cargaron ${docsParaCargar.length} documentos a la base de datos.`);
      
      // Actualizar estado de documentos existentes
      const nuevosExistentes = {...documentosExistentes};
      docsParaCargar.forEach((doc) => {
        const docIndex = documentos.findIndex(d => 
          d.rut_proveedor === doc.rut_proveedor && 
          d.folio === doc.folio
        );
        if (docIndex >= 0) {
          nuevosExistentes[docIndex.toString()] = true;
        }
      });
      setDocumentosExistentes(nuevosExistentes);
      
      // Limpiar selecciones
      setDocumentosSeleccionados({});
      
      // Navegar a la página de documentos después de la carga exitosa
      setTimeout(() => {
        navigate('/rcv/documentos');
      }, 1500);
    } catch (err) {
      setError(err.message || 'Error al cargar documentos en la base de datos');
    } finally {
      setLoadingCarga(false);
    }
  };

  const handleToggleSeleccion = (id) => {
    if (documentosExistentes[id]) return; // No permitir seleccionar documentos que ya existen
    
    setDocumentosSeleccionados(prev => ({
      ...prev,
      [id]: !prev[id]
    }));
  };

  const handleSeleccionarTodos = (event) => {
    if (event.target.checked) {
      // Seleccionar todos excepto los que ya existen
      const seleccion = {};
      documentos.forEach((doc, index) => {
        if (!documentosExistentes[index.toString()]) {
          seleccion[index.toString()] = true;
        }
      });
      setDocumentosSeleccionados(seleccion);
    } else {
      setDocumentosSeleccionados({});
    }
  };

  // Contar cuántos nuevos hay y cuántos seleccionados
  const contarDocumentos = () => {
    const total = documentos.length;
    const existentes = Object.keys(documentosExistentes).length;
    const nuevos = total - existentes;
    const seleccionados = Object.keys(documentosSeleccionados).filter(k => documentosSeleccionados[k]).length;
    
    return { total, existentes, nuevos, seleccionados };
  };

  const { total, existentes, nuevos, seleccionados } = contarDocumentos();

  return (
    <CardContainer
      title="Cargar RCV desde CSV"
      subheader="Cargue datos del Registro de Compras y Ventas desde un archivo CSV"
    >
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} md={9}>
            <TextField
              label="RUT Empresa"
              placeholder="Ej: 77123456"
              fullWidth
              value={rutEmpresa}
              disabled={true} // Campo deshabilitado
              required
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              label="DV"
              placeholder="Ej: K"
              fullWidth
              value={dvEmpresa}
              disabled={true} // Campo deshabilitado
              required
            />
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="periodo-label">Período</InputLabel>
              <Select
                labelId="periodo-label"
                id="periodo"
                value={periodo}
                label="Período"
                onChange={(e) => setPeriodo(e.target.value)}
              >
                {periodos.map((opcion) => (
                  <MenuItem key={opcion.value} value={opcion.value}>
                    {opcion.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="tipo-operacion-label">Tipo Operación</InputLabel>
              <Select
                labelId="tipo-operacion-label"
                id="tipo-operacion"
                value={tipoOperacion}
                label="Tipo Operación"
                onChange={(e) => setTipoOperacion(e.target.value)}
              >
                <MenuItem value="COMPRA">Compra</MenuItem>
                <MenuItem value="VENTA">Venta</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="estado-contab-label">Estado Contable</InputLabel>
              <Select
                labelId="estado-contab-label"
                id="estado-contab"
                value={estadoContab}
                label="Estado Contable"
                onChange={(e) => setEstadoContab(e.target.value)}
              >
                <MenuItem value="REGISTRO">Registro</MenuItem>
                <MenuItem value="PENDIENTE">Pendiente</MenuItem>
                <MenuItem value="NO_INCLUIR">No Incluir</MenuItem>
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            Seleccionar archivo CSV
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            Seleccione un archivo CSV con los datos del RCV.
            El archivo debe tener los mismos encabezados que los obtenidos desde el SII:
            (Nro;Tipo Doc;Tipo Compra;RUT Proveedor;Razon Social;Folio;Fecha Docto;etc...)
          </Typography>
          
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                startIcon={<UploadFile />}
                sx={{ py: 1.5 }}
              >
                {csvFile ? csvFile.name : 'Seleccionar archivo CSV...'}
                <input
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={handleFileChange}
                />
              </Button>
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                onClick={() => parseCSV(csvFile)}
                disabled={!csvFile || loadingParsing}
                startIcon={loadingParsing ? <CircularProgress size={20} /> : <FilePresent />}
              >
                {loadingParsing ? 'Procesando CSV...' : 'Procesar CSV'}
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>
        )}

        <LoadingState loading={loading} loadingMessage="Verificando documentos existentes...">

        {/* Tabla con documentos cargados desde CSV */}
        {documentos.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Paper sx={{ p: 2, mb: 2 }}>
              <Typography variant="h6" gutterBottom>
                Documentos Cargados desde CSV
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Chip label={`Total: ${total}`} color="primary" variant="outlined" />
                <Chip label={`Existentes: ${existentes}`} color="default" variant="outlined" />
                <Chip label={`Nuevos: ${nuevos}`} color="success" variant="outlined" />
                <Chip label={`Seleccionados: ${seleccionados}`} color="secondary" variant="outlined" />
              </Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Seleccione los documentos que desea cargar a la base de datos. Los documentos ya existentes aparecen inhabilitados.
              </Typography>
            </Paper>
            
            <TableContainer component={Paper} sx={{ maxHeight: 400 }}>
              <Table stickyHeader size="small">
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox
                        indeterminate={seleccionados > 0 && seleccionados < nuevos}
                        checked={nuevos > 0 && seleccionados === nuevos}
                        onChange={handleSeleccionarTodos}
                        disabled={nuevos === 0}
                      />
                    </TableCell>
                    <TableCell>Tipo</TableCell>
                    <TableCell>RUT Proveedor</TableCell>
                    <TableCell>Razón Social</TableCell>
                    <TableCell>Folio</TableCell>
                    <TableCell>Fecha</TableCell>
                    <TableCell align="right">Monto</TableCell>
                    <TableCell align="center">Estado</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {documentos.map((doc, index) => {
                    const isSelected = documentosSeleccionados[index.toString()];
                    const isExisting = documentosExistentes[index.toString()];
                    
                    return (
                      <TableRow
                        key={`${doc.rut_proveedor}-${doc.folio}-${index}`}
                        hover
                        selected={isSelected}
                        sx={{ opacity: isExisting ? 0.6 : 1 }}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={isSelected || false}
                            onChange={() => handleToggleSeleccion(index.toString())}
                            disabled={isExisting}
                          />
                        </TableCell>
                        <TableCell>
                          {getTipoDocLabel(doc.tipo_doc)}
                        </TableCell>
                        <TableCell>
                          {formatearRut(doc.rut_proveedor, doc.dv_proveedor)}
                        </TableCell>
                        <TableCell>
                          <Tooltip title={doc.razon_social || ''}>
                            <Typography variant="body2" noWrap sx={{ maxWidth: 150 }}>
                              {doc.razon_social}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>{doc.folio}</TableCell>
                        <TableCell>{formatearFecha(doc.fecha_docto)}</TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontFamily="monospace">
                            {formatearMonto(doc.monto_total)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          {isExisting ? (
                            <Chip 
                              size="small" 
                              label="Ya existe" 
                              color="default" 
                              variant="filled" 
                            />
                          ) : (
                            <Chip 
                              size="small" 
                              label="Nuevo" 
                              color="success" 
                              variant="outlined" 
                            />
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
              <Button
                variant="outlined"
                color="error"
                startIcon={<CancelOutlined />}
                onClick={() => {
                  setDocumentos([]);
                  setDocumentosSeleccionados({});
                  setDocumentosExistentes({});
                  setSuccess('');
                }}
              >
                Cancelar
              </Button>
              
              <Button
                variant="contained"
                color="secondary"
                startIcon={loadingCarga ? <CircularProgress size={20} /> : <SaveOutlined />}
                onClick={handleCargarDocumentos}
                disabled={loadingCarga || seleccionados === 0}
              >
                {loadingCarga ? 'Cargando...' : `Cargar ${seleccionados} documento(s)`}
              </Button>
            </Box>
          </Box>
        )}
        </LoadingState>

        <Divider sx={{ my: 2 }} />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 2 }}>
          * Formato esperado del CSV: datos separados por punto y coma (;) con encabezados que coincidan con los campos del RCV del SII.
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
          * Para consultar el modelo de archivo, primero exporte un RCV desde el portal del SII y utilícelo como plantilla.
        </Typography>
    </CardContainer>
  );
};

export default CsvUploadTab;